import React from 'react';
import { Redirect } from 'react-router-dom';
import { Visualization } from '@bb-ui/icons/dist/medium';
import { Course } from '@bb-ui/icons/dist/small';
import { useTranslation } from 'react-i18next';
import { NavigationRoutedLink } from '../NavigationRouting/NavigationRoutedLink';
import { NavigationRouted } from '../NavigationRouting/NavigationRouted';
import { DeveloperPage } from '../DeveloperPage/DeveloperPage';
import { useApi } from '../../hooks/useApi';
import { ISubmodel } from '../../types/appUITypes';
import { useAppConfigContext } from '../../contexts/AppConfigProvider';
import { IERDData } from '../DataDictionary/ERD/InteractiveERD/InteractiveERD.types';
import { IDataDictionaryEntry } from '../DataDictionary/Entries/DataDictionaryEntries/DataDictionaryEntries.types';
import { useAccessControl } from '../../hooks/useAccessControl';
import { useFeatureContext } from '../../contexts/FeatureContext';
import { xconsole } from '../../utilities/utilities';

const DataDictionary = React.lazy(
    () =>
        import(
            /* webpackChunkName: 'datadictionary' */ '../DataDictionary/DataDictionary'
        ),
);

export const DeveloperContent: React.FC<{
    navigationIndex: number;
}> = props => {
    const { t } = useTranslation();
    const access = useAccessControl();
    const { api } = useAppConfigContext();
    const features = useFeatureContext();
    const isDataDictionaryRedesignEnabled = Boolean(
        features?.dataDictionaryNavigationRedesign,
    );

    const {
        data: submodels,
        loading: submodelsLoading,
        error: submodelsError,
    } = useApi<ISubmodel[]>({
        url: `${api!.dataDictionary}/v1/data/dictionary/submodels`,
        useAuth: false,
        transformResponse: (data: ISubmodel[]) =>
            data.sort((a, b) => a.schemaId.localeCompare(b.schemaId)),
    });

    const {
        data: ERDData,
        loading: ERDDataLoading,
        error: ERDDataError,
    } = useApi<IERDData>({
        url: `${api!.dataDictionary}/v1/data/dictionary/erd`,
        useAuth: false,
    });

    const {
        data: dictionaryEntries,
        loading: dictionaryEntriesLoading,
        error: dictionaryEntriesError,
    } = useApi<IDataDictionaryEntry[]>({
        url: `${api!.dataDictionary}/v1/data/dictionary/definitions`,
        useAuth: false,
        transformResponse: (data: IDataDictionaryEntry[]) =>
            data.filter((entry: IDataDictionaryEntry) => {
                if (!entry)
                    xconsole.warn(
                        'A `null` entry was found in the Data Dictionary. Null entries will not be displayed.',
                    );
                return !!entry;
            }),
    });

    return (
        <NavigationRoutedLink
            key="developer"
            path="developer"
            label={t('developer.title')}
            icon={<Visualization />}
            index={props.navigationIndex}
            access={access.developer}
        >
            <NavigationRouted>
                <DeveloperPage />
            </NavigationRouted>

            <NavigationRoutedLink
                key="dictionary"
                path="dictionary"
                label={t('dataDictionary.title')}
                icon={<Course />}
                routeWithoutContext
                index={1}
                exact={false}
                access={access['developer/data-dictionary']}
                hideSecondaryNavigation={
                    isDataDictionaryRedesignEnabled || undefined
                }
                secondaryNavOwnedElsewhere={
                    isDataDictionaryRedesignEnabled || undefined
                }
            >
                <NavigationRoutedLink
                    path="entries"
                    index={1}
                    label={t('dataDictionary.entries')}
                    exact={false}
                    access={access['developer/data-dictionary']}
                    hideSecondaryNavigation={
                        isDataDictionaryRedesignEnabled || undefined
                    }
                    secondaryNavOwnedElsewhere={
                        isDataDictionaryRedesignEnabled || undefined
                    }
                >
                    <NavigationRouted exact={false}>
                        <DataDictionary
                            variant="entries"
                            ERDData={ERDData}
                            dictionaryEntries={dictionaryEntries}
                            submodels={submodels}
                            loading={
                                ERDDataLoading ||
                                submodelsLoading ||
                                dictionaryEntriesLoading
                            }
                            error={
                                ERDDataError ||
                                submodelsError ||
                                dictionaryEntriesError
                            }
                        />
                    </NavigationRouted>
                </NavigationRoutedLink>

                <NavigationRoutedLink
                    path="entity-relationships"
                    index={2}
                    label={t('dataDictionary.entityRelationships.tabText')}
                    exact={false}
                    access={access['developer/data-dictionary']}
                    hideSecondaryNavigation={
                        isDataDictionaryRedesignEnabled || undefined
                    }
                    secondaryNavOwnedElsewhere={
                        isDataDictionaryRedesignEnabled || undefined
                    }
                >
                    <NavigationRouted exact={false}>
                        <DataDictionary
                            variant="erd"
                            ERDData={ERDData}
                            dictionaryEntries={dictionaryEntries}
                            submodels={submodels}
                            loading={
                                ERDDataLoading ||
                                submodelsLoading ||
                                dictionaryEntriesLoading
                            }
                            error={
                                ERDDataError ||
                                submodelsError ||
                                dictionaryEntriesError
                            }
                        />
                    </NavigationRouted>
                </NavigationRoutedLink>

                {isDataDictionaryRedesignEnabled && (
                    <NavigationRoutedLink
                        path="search"
                        index={3}
                        label={t('dataDictionary.search.tabText')}
                        exact={false}
                        access={access['developer/data-dictionary']}
                        hideSecondaryNavigation
                        secondaryNavOwnedElsewhere
                    >
                        <NavigationRouted exact={false}>
                            <DataDictionary
                                variant="search"
                                ERDData={ERDData}
                                dictionaryEntries={dictionaryEntries}
                                submodels={submodels}
                                loading={
                                    ERDDataLoading ||
                                    submodelsLoading ||
                                    dictionaryEntriesLoading
                                }
                                error={
                                    ERDDataError ||
                                    submodelsError ||
                                    dictionaryEntriesError
                                }
                            />
                        </NavigationRouted>
                    </NavigationRoutedLink>
                )}

                {isDataDictionaryRedesignEnabled && (
                    <NavigationRoutedLink
                        path="schemas"
                        index={4}
                        label={t('dataDictionary.navigation.schemasLabel')}
                        exact={false}
                        access={access['developer/data-dictionary']}
                        hideSecondaryNavigation
                        secondaryNavOwnedElsewhere
                    >
                        <NavigationRouted exact={false}>
                            <DataDictionary
                                variant="schemas"
                                ERDData={ERDData}
                                dictionaryEntries={dictionaryEntries}
                                submodels={submodels}
                                loading={
                                    ERDDataLoading ||
                                    submodelsLoading ||
                                    dictionaryEntriesLoading
                                }
                                error={
                                    ERDDataError ||
                                    submodelsError ||
                                    dictionaryEntriesError
                                }
                            />
                        </NavigationRouted>
                    </NavigationRoutedLink>
                )}

                <NavigationRouted>
                    <Redirect
                        to={
                            isDataDictionaryRedesignEnabled
                                ? 'dictionary/schemas'
                                : 'dictionary/entries'
                        }
                    />
                </NavigationRouted>
            </NavigationRoutedLink>
        </NavigationRoutedLink>
    );
};
